import { changeDescription } from 'flow/store/redux/types';
import type {
  ChangeDescription,
  UiEffectContext,
  UiEffectContextByTarget,
  WorkflowDiff,
  WorkflowHistoryEntry,
} from 'flow/store/redux/types';
import { Operation } from 'flow/service/changelog/types';
import { generatePublicId } from '../publicIds';
import { ActionType } from 'flow/store/engine/constants';

/**
 * Registry of per-target ui effect context util builders. Add a new entry here (and to `UiEffectContextByTarget`)
 * to support building another kind of UI effect context in tests.
 */
const uiEffectContextBuilders: {
  [T in ChangeDescription]: (
    overrides: Partial<UiEffectContextByTarget[T]>
  ) => UiEffectContextByTarget[T];
} = {
  [changeDescription.action]: overrides => ({
    publicId: generatePublicId('action', ActionType.INPUT),
    ...overrides,
    target: changeDescription.action,
  }),
  [changeDescription.threshold]: overrides => ({
    publicId: generatePublicId('threshold'),
    actionId: generatePublicId('action', ActionType.TAG),
    ...overrides,
    target: changeDescription.threshold,
  }),
  [changeDescription.group]: overrides => ({
    actionId: generatePublicId('action', ActionType.TAG),
    ...overrides,
    target: changeDescription.group,
  }),
};

/**
 * Builds a `UiEffectContext` for tests. The return type narrows to the context that matches
 * the requested `target`, with sensible defaults that can be individually overridden.
 */
export function makeUiEffectContext<T extends ChangeDescription>(
  target: T,
  overrides: Partial<UiEffectContextByTarget[T]> = {},
): UiEffectContextByTarget[T] {
  const build = uiEffectContextBuilders[target];

  return build(overrides);
}

export function makeWorkflowHistoryEntry({
  diff,
  operation,
  uiEffectContext,
}: {
  diff?: WorkflowDiff;
  operation?: Operation;
  uiEffectContext?: UiEffectContext;
} = {}): WorkflowHistoryEntry {
  return {
    diff: diff ?? {
      workflowData: {},
      workflowProps: null,
    },
    operation: operation ?? Operation.Add,
    uiEffectContext: uiEffectContext ?? makeUiEffectContext(changeDescription.action),
  };
}
